import { gql } from '@apollo/client';
import React, { useCallback, useMemo } from 'react';

import type { LinkProps } from '../../components/Link';
import { frontConfig } from '../../config';
import { useAppApolloClient } from '../../hooks/useAppApolloClient';
import { useIsMine, usePermissions } from '../../hooks/usePermissions';
import { useShortUrl } from '../../hooks/useShortUrl';
import { useWebsite, useWebsiteMeta } from '../../hooks/useWebsite';
import { useAppDispatch } from '../../redux/hooks';
import type { VoteRef } from '../../services/scrollSections';
import type { Author, Message, Thread, User, Vote } from '../../types/api';
import type { DeleteResponseStatusFragment } from '../api/message';
import { AnswerBottomAction } from '../BottomActions/AnswerBottomAction';
import { ShareBottomAction } from '../BottomActions/ShareBottomAction';
import { VoteBottomAction } from '../BottomActions/VoteBottomAction';
import { useWithoutAnswers } from '../Discussion/WithoutAnswersProvider';
import { BubbleFlowButton } from '../HeaderActions/BubbleFlowButton';
import { CopyLinkOption } from '../HeaderActions/CopyLinkOption';
import { DeleteMessageOption } from '../HeaderActions/DeleteMessageOption';
import { EditOption } from '../HeaderActions/EditOption';
import { AdminDeleteMessageOption } from '../HeaderActions/remove/AdminDeleteMessageOption';
import { BanAuthorOption } from '../HeaderActions/report/BanAuthorOption';
import { ReportMenuItem } from '../HeaderActions/report/ReportMenuItem';
import { SpamOption } from '../HeaderActions/report/SpamOption';
import { useThreadContext } from '../ThreadContexts/ThreadContext';
import { useMessageRedux } from '../ThreadMessage/thread-service';
import { updateMessage } from '../ThreadMessage/thread-slice';
import {
  ChildMessageMetaFragment,
  ChildMessageRelevanceFragment,
  generateChildren,
  ThreadMessageFragment,
} from '../ThreadMessages/query';
import { MessageTopic } from '../Topic/MessageTopic';
import type { VoteResp } from '../Vote/vote-model';
import { ThreadInteractiveContent } from './ThreadInteractiveContent';

type ThreadInteractiveMessageProps = {
  mode?: 'nested';
  messageId?: string | undefined;
  message?: Message;
  answeredAuthor?: Author;
  startEditing?: () => void;
  answer?: () => void;
  voteRef?: VoteRef;
  directLink?: Partial<LinkProps>;
  unlimited?: boolean;
  withTopic?: boolean;
  withTopIcon?: boolean;
  isNestedAnswer?: boolean;
  onDelete?: (message: Message) => void;
  shouldHandleDsaDisplay?: boolean;
  isYakliDisplay?: boolean;
};

export const ThreadInteractiveMessage: React.FC<ThreadInteractiveMessageProps> = ({
  mode,
  messageId,
  message: messageProp, // For legacy usage outside redux. Once deleted, make messageId required.
  answer,
  answeredAuthor,
  startEditing,
  directLink,
  voteRef,
  unlimited,
  withTopic,
  withTopIcon = true,
  isNestedAnswer,
  onDelete,
  shouldHandleDsaDisplay = false,
  isYakliDisplay = false,
}) => {
  const { isAdmin, isModerator, authUserId } = usePermissions();
  const message = (useMessageRedux(messageId, isYakliDisplay) || messageProp) as Message;
  const author = message?.author as User;
  const isMine = useIsMine(author);
  const { id: websiteId } = useWebsite();
  const { topic: topicMeta = {} } = useWebsiteMeta('topic');

  // TODO: fix-me or question the 'nofollow' rel attribute of topic link
  // @ts-ignore
  const { index: shouldIndexTopic = false } = topicMeta ?? {};
  // const { slug } = message?.thread?.topic || {};
  const { slug } = useThreadContext();
  const shortUrl = useShortUrl({ commentId: message.id as string, slug });

  // @TODO remove tmp while necessary for api
  let threadIdTmp: string | undefined = useThreadContext().threadId;
  if (!threadIdTmp) {
    threadIdTmp = message.thread?.id;
  }
  if (!threadIdTmp) {
    throw new Error('Cannot render a message without its thread ID');
  }
  let threadId = threadIdTmp;

  const client = useAppApolloClient();
  const dispatch = useAppDispatch();

  const onVote = useCallback(
    async (data2: VoteResp) => {
      const partial: Partial<Message> = {
        id: data2._id,
        votes: data2.voters.map(v => ({
          score: v.score,
          user: { __typename: 'User', id: v.user },
        })),
      };
      dispatch(updateMessage({ message: partial }));
    },
    [dispatch],
  );

  const onRemove = useCallback(
    (_data: DeleteResponseStatusFragment) => {
      onDelete?.(message);
    },
    [message, onDelete],
  );

  const hasChildren =
    client.readFragment({
      fragment: gql`
                fragment HasChildrenFrg on Message {
                    id
                    ${generateChildren(1, 'relevance')}
                }
                ${ChildMessageMetaFragment}
                ${ThreadMessageFragment}
                ${ChildMessageRelevanceFragment}
            `,
      fragmentName: 'HasChildrenFrg',
      id: `Message:${message.id}`,
    })?.childMessages?.messages?.length > 0;

  const edition = useMemo(
    () => (
      <>
        {!isMine && <ReportMenuItem object="comment" id={message?.id as string} />}
        <CopyLinkOption shortUrl={shortUrl} />
        {isMine && startEditing && <EditOption startEditing={startEditing} />}
        {isMine && (
          <DeleteMessageOption
            messageId={message.id as string}
            content={message.content as string}
            onRemove={onRemove}
          />
        )}
      </>
    ),
    [shortUrl, isMine, startEditing, message.id, message.content, onRemove],
  );

  const admin = useMemo(
    () =>
      isAdmin ? (
        <>
          {!isMine && <SpamOption threadId={threadId} commentId={message.id as string} />}
          {!isMine && <BanAuthorOption userId={author?.id} sourceId={message.id} />}
          <AdminDeleteMessageOption
            hasChildren={hasChildren}
            messageId={message.id as string}
            content={message.content as string}
            threadId={threadId}
            websiteId={websiteId as string}
            onRemove={onRemove}
            status={message.status as string}
          />
        </>
      ) : null,
    [isAdmin, isMine, message, author, onRemove, threadId, websiteId, hasChildren],
  );

  const moderator = useMemo(
    () =>
      isModerator ? (
        <>
          {!isMine && <SpamOption threadId={threadId} commentId={message.id as string} />}
          {!isMine && <BanAuthorOption userId={author?.id} sourceId={message.id} />}
          <AdminDeleteMessageOption
            hasChildren={hasChildren}
            messageId={message.id as string}
            content={message.content as string}
            threadId={threadId}
            websiteId={websiteId as string}
            onRemove={onRemove}
            status={message.status as string}
            asModerator={true}
          />
        </>
      ) : null,
    [isModerator, isMine, message, author, onRemove, threadId, websiteId, hasChildren],
  );
  const showBubbleFlowButton =
    withTopIcon && !!(message?.parentMessage || message.childMessages?.meta?.count);

  const withoutAnswers = useWithoutAnswers();

  return (
    <ThreadInteractiveContent
      messageId={messageId}
      shouldHandleDsaDisplay={shouldHandleDsaDisplay}
      mode={mode}
      author={message.author as Author}
      answeredAuthor={answeredAuthor}
      date={message.createdAt}
      content={message.content as string}
      embeds={message.embeds}
      status={message.status as string}
      votes={message.votes as Vote[]}
      options={{ edition, moderator, admin }}
      isNestedAnswer={isNestedAnswer}
      topIcon={
        showBubbleFlowButton && (
          <BubbleFlowButton
            message={message}
            shouldIndex={shouldIndexTopic}
            linkProps={directLink}
          />
        )
      }
      actions={
        <>
          {frontConfig.showMessageId && <span className="self-center">{message.id}</span>}
          <ShareBottomAction
            className="py-0"
            content={message.content as string}
            shortUrl={shortUrl}
          />
          {authUserId !== author?.id && threadId ? (
            <VoteBottomAction
              className="py-0"
              currentVote={
                (message.votes || []).find(elt => (elt as Vote)?.user?.id === authUserId) as Vote
              }
              url={`/api/thread/${threadId}/comment/vote/${message.id}`}
              onVote={onVote}
              ref={voteRef}
            />
          ) : null}
          {!withoutAnswers && answer ? (
            <AnswerBottomAction className="py-0 pr-0" answer={answer} />
          ) : null}
        </>
      }
      unlimited={unlimited}
      topicSource={
        withTopic && (
          <MessageTopic
            thread={message.thread as Thread}
            aClassName="text-sm text-gray-medium"
            shouldIndex={shouldIndexTopic}
            prefix
            withLink
          />
        )
      }
    />
  );
};
